import React from 'react';
import { PHASE_LABELS, type BreadcrumbPhase, type StairBand } from '../lib/breadcrumbsCatalog';
import type { FunnelResult, FunnelRow } from '../lib/breadcrumbFunnelService';

const BAND_COPY: Record<StairBand, { title: string; hint: string }> = {
  pre: {
    title: 'Avant le compte',
    hint: 'Visiteurs (inscrits ou non). La barre se rétrécit : on ne garde que ceux qui ont aussi fait les étapes au-dessus.',
  },
  stair: {
    title: 'Escalier des inscrits',
    hint: 'Parcours obligatoire. Chaque barre = ceux qui ont franchi cette marche ET toutes celles au-dessus. Elle ne peut que raccourcir.',
  },
  fork: {
    title: 'Fourche',
    hint: 'Chemin alternatif, pas une marche de plus dans l’escalier.',
  },
  milestone: {
    title: 'Jalons (dans n’importe quel ordre)',
    hint: 'Actions optionnelles parmi les inscrits. Liker n’exige pas d’avoir cherché : une barre plus longue que la voisine n’est pas un retour en arrière.',
  },
};

function barColor(band: StairBand): string {
  if (band === 'fork') return 'bg-amber-400';
  if (band === 'milestone') return 'bg-slate-400';
  if (band === 'pre') return 'bg-sky-500';
  return 'bg-indigo-500';
}

function StairRow({ row }: { row: FunnelRow }) {
  const width = Math.max(row.reached > 0 ? 2 : 0, Math.min(100, row.barPct));
  const showCohortPct = row.band !== 'pre';
  return (
    <div className={`flex items-center gap-3 py-1 ${row.fork ? 'opacity-90' : ''}`}>
      <span className="w-7 text-right text-[11px] text-gray-400 font-mono">{row.n}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm text-gray-800 truncate">{row.label}</span>
          {row.fork && (
            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
              split
            </span>
          )}
        </div>
        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${barColor(row.band)}`}
            style={{ width: `${width}%` }}
            title={
              row.band === 'milestone' || row.band === 'fork'
                ? `${row.reached} inscrits ont fait cette action (${row.pctOfCohort} % de la cohorte)`
                : row.reachedAny > row.reached
                  ? `${row.reached} encore dans l’escalier · ${row.reachedAny} ont touché cette étape en sautant des marches`
                  : `${row.reached} personnes encore présentes à cette marche (${row.barPct} % du haut de l’escalier)`
            }
          />
        </div>
      </div>
      <span className="w-10 text-right text-sm font-semibold text-gray-800">{row.reached}</span>
      <span className="w-14 text-right text-xs text-gray-500">
        {showCohortPct ? `${row.pctOfCohort}%` : '—'}
      </span>
      <span className="w-16 text-right text-xs text-gray-400">
        {row.dropOff == null ? '—' : row.dropOff <= 0 ? '0%' : `−${row.dropOff}%`}
      </span>
      <span className="w-16 text-right text-xs text-gray-400">
        {row.medianHoursToNext == null ? '—' : `${row.medianHoursToNext} h`}
      </span>
    </div>
  );
}

export function BreadcrumbStair({ data }: { data: FunnelResult }) {
  return (
    <div className="p-6 space-y-4">
      <div>
        <p className="text-sm text-gray-600">
          Cohorte : <span className="font-semibold text-gray-900">{data.cohort}</span>
          {data.role === 'candidate' ? ' candidats' : ' recruteurs'} inscrits sur la période
        </p>
        <p className="text-xs text-gray-500 mt-1 max-w-3xl">
          La barre n’est pas un volume d’événements. Sur un escalier, c’est la part de gens encore là
          après les marches précédentes. Sur un jalon, c’est la part des inscrits qui ont fait cette
          action au moins une fois.
        </p>
      </div>

      <div className="flex items-center gap-3 text-[10px] uppercase tracking-wide text-gray-400 font-semibold px-[calc(1.75rem+0.75rem)]">
        <span className="flex-1" />
        <span className="w-10 text-right">Pers.</span>
        <span className="w-14 text-right">% inscrits</span>
        <span className="w-16 text-right">Perte</span>
        <span className="w-16 text-right">Délai</span>
      </div>

      <div className="space-y-1">
        {data.rows.map((row, i) => {
          const prev = i > 0 ? data.rows[i - 1] : null;
          const bandChanged = !prev || prev.band !== row.band;
          const phaseChanged = !bandChanged && prev != null && prev.phase !== row.phase;
          const copy = BAND_COPY[row.band];
          const stairTitle =
            row.band === 'stair' && prev?.band === 'milestone' ? 'Escalier publication' : copy.title;
          return (
            <div key={row.id}>
              {bandChanged && (
                <div className="pt-4 pb-1">
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">
                    {stairTitle}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{copy.hint}</p>
                </div>
              )}
              {phaseChanged && (
                <p className="text-[10px] uppercase tracking-widest text-gray-300 font-semibold pt-2 pb-0.5">
                  {PHASE_LABELS[row.phase as BreadcrumbPhase] || row.phase}
                </p>
              )}
              <StairRow row={row} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
